import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Layout from '../Components/Layout';
import PaginatedItems from "../Components/Pagination";

const PurchasingLibrary = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [sortBy, setSortBy] = useState('id');
    const [sortOrder, setSortOrder] = useState('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const searchTimerRef = useRef(null);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearch(value);
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        searchTimerRef.current = setTimeout(() => {
            setDebouncedSearch(value);
        }, 300);
    };

    useEffect(() => {
        return () => {
            if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        };
    }, []);

    const fetchData = async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                page: page.toString(),
                limit: '10',
                sort_by: sortBy,
                sort_order: sortOrder
            };
            
            if (search) params.search = search;
            
            const response = await axios.get('/library/purchase-items', { params });
            const result = response.data;
            
            if (result.success) {
                setData(result.data);
                setCurrentPage(result.pagination.current_page);
                setTotalPages(result.pagination.total_pages);
                setTotalRecords(result.pagination.total_records);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(1);
    }, [debouncedSearch, sortBy, sortOrder]);

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('asc');
        }
    };

    const handlePageChange = (page) => {
        fetchData(page);
    };

    const paginatePage = (page) => {
        console.log(`pageNumber ! ${page}`)
        handlePageChange(page);
    };

    return (
        <Layout>
            <div style={{ height: 'calc(100vh - 70px)', overflow: 'auto', padding: '20px 0px' }}>
                <div className="container-fluid purchasing-library">
            {/* Search and Actions Bar */}
            <div className="row mb-3 p-3 bg-light">
                <div className="col-md-6">
                    <div className="input-group">
                        <span className="input-group-text">
                            <i className="ti-search"></i>
                        </span>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search items, descriptions, vendors..."
                            value={search}
                            onChange={handleSearchChange}
                        />
                    </div>
                </div>
                <div className="col-md-6 text-end">
                    <small className="text-muted">
                        {totalRecords} items found
                    </small>
                </div>
            </div>

            {/* Data Table with Horizontal Scroll */}
            <div className="table-responsive" style={{ overflowX: 'auto' }}>
                {loading ? (
                    <div className="text-center p-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden"></span>
                        </div>
                        <p className="mt-2">Loading purchasing library...</p>
                    </div>
                ) : (
                    <table className="table table-hover" style={{ minWidth: '1400px' }}>
                        <thead className="table-light">
                            <tr>
                                <th 
                                    onClick={() => handleSort('id')}
                                    style={{ cursor: 'pointer', minWidth: '60px' }}
                                >
                                    ID {sortBy === 'id' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                                <th 
                                    onClick={() => handleSort('ActiveStatus')}
                                    style={{ cursor: 'pointer', minWidth: '100px' }}
                                >
                                    Status {sortBy === 'ActiveStatus' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                                <th 
                                    onClick={() => handleSort('Item')}
                                    style={{ cursor: 'pointer', minWidth: '150px' }}
                                >
                                    Item {sortBy === 'Item' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                                <th 
                                    onClick={() => handleSort('Description')}
                                    style={{ cursor: 'pointer', minWidth: '250px' }}
                                >
                                    Description {sortBy === 'Description' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                                <th 
                                    onClick={() => handleSort('PreferredVendor')}
                                    style={{ cursor: 'pointer', minWidth: '150px' }}
                                >
                                    Preferred Vendor {sortBy === 'PreferredVendor' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                                <th 
                                    onClick={() => handleSort('Cost')}
                                    style={{ cursor: 'pointer', minWidth: '100px' }}
                                >
                                    Cost {sortBy === 'Cost' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                                <th 
                                    onClick={() => handleSort('U_M')}
                                    style={{ cursor: 'pointer', minWidth: '80px' }}
                                >
                                    U/M {sortBy === 'U_M' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                                <th 
                                    onClick={() => handleSort('MOQ')}
                                    style={{ cursor: 'pointer', minWidth: '80px' }}
                                >
                                    MOQ {sortBy === 'MOQ' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                                <th 
                                    onClick={() => handleSort('PackSize')}
                                    style={{ cursor: 'pointer', minWidth: '100px' }}
                                >
                                    Pack Size {sortBy === 'PackSize' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                                <th 
                                    onClick={() => handleSort('LeadTime')}
                                    style={{ cursor: 'pointer', minWidth: '100px' }}
                                >
                                    Lead Time {sortBy === 'LeadTime' && (
                                        <i className={`ti-arrow-${sortOrder === 'asc' ? 'up' : 'down'}`}></i>
                                    )}
                                </th>
                                <th style={{ minWidth: '120px' }}>Tier Qty 1 Price</th>
                                <th style={{ minWidth: '120px' }}>Tier Qty 2 Price</th>
                                <th style={{ minWidth: '120px' }}>Tier Qty 3 Price</th>
                                <th style={{ minWidth: '100px' }}>Date</th>
                                <th style={{ minWidth: '120px' }}>Ship Notes</th>
                                <th style={{ minWidth: '100px' }}>FOB/DDP/CIF</th>
                                <th style={{ minWidth: '100px' }}>Ship Point</th>
                                <th style={{ minWidth: '150px' }}>Notes</th>
                                <th style={{ minWidth: '120px' }}>Approv Man 1</th>
                                <th style={{ minWidth: '120px' }}>Approv Man 2</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((item) => (
                                <tr key={item.id}>
                                    <td>{item.id}</td>
                                    <td>{item.ActiveStatus}</td>
                                    <td>{item.Item}</td>
                                    <td>{item.Description}</td>
                                    <td>{item.PreferredVendor}</td>
                                    <td>{item.Cost}</td>
                                    <td>{item.U_M}</td>
                                    <td>{item.MOQ}</td>
                                    <td>{item.PackSize}</td>
                                    <td>{item.LeadTime}</td>
                                    <td>{item.TierQty1Price}</td>
                                    <td>{item.TierQty2Price}</td>
                                    <td>{item.TierQty3Price}</td>
                                    <td>{item.Date}</td>
                                    <td>{item.ShipNotes}</td>
                                    <td>{item.FOB_DDP_CIF}</td>
                                    <td>{item.ShipPoint}</td>
                                    <td>{item.Notes}</td>
                                    <td>{item.ApprovMan1}</td>
                                    <td>{item.ApprovMan2}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Pagination - Using PaginatedItems Component */}
            {!loading && totalRecords > 0 && (
                <div className="col-lg-12 mb-12" style={{marginTop:'20px'}}>
                    <PaginatedItems total={totalRecords} onPageChange={paginatePage} currentPage={currentPage}></PaginatedItems>
                </div>
            )}
                </div>
            </div>
        </Layout>
    );
};

export default PurchasingLibrary;